/**
 * analytics-shared/api/foldersApi.js
 *
 * Folder CRUD shared by reports + dashboards. After wedge 2 both halves
 * are backed by /api/v1 (the analyticsClient). Two distinct shapes remain
 * on the wire to match what the existing UI consumes:
 *
 *   Reports:    ReportFolder objects { id, name, color, ... }
 *   Dashboards: flat list of strings
 *
 * useFolders.js consumes these surfaces unchanged — the only behaviour
 * difference vs. the localStorage era is that creates/renames/deletes
 * now survive a refresh and propagate across users in the same tenant.
 */
import { analyticsClient, unwrap } from './client';

const uid = (p = 'fld') => `${p}_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`;

export const foldersApi = {
  // ─── Reports (object-shaped folders) ────────────────────────────────────
  listReportFolders: async () => analyticsClient.get('/report-folders').then(unwrap),

  createReportFolder: async (name) => analyticsClient
    .post('/report-folders', { name, color: 'slate' })
    .then(unwrap),

  renameReportFolder: async (id, name) => analyticsClient
    .patch(`/report-folders/${encodeURIComponent(id)}`, { name })
    .then(unwrap),

  // Optional colour update — kept here so the UI can swap palette without
  // a separate API surface.
  recolorReportFolder: async (id, color) => analyticsClient
    .patch(`/report-folders/${encodeURIComponent(id)}`, { color })
    .then(unwrap),

  deleteReportFolder: async (id) => analyticsClient
    .delete(`/report-folders/${encodeURIComponent(id)}`)
    .then(unwrap),

  // ─── Dashboards (string-shaped folders) ─────────────────────────────────
  // Server returns string[]; create/rename echo back the name so the
  // optimistic updates in useDashboardFolders keep working unchanged.
  listDashboardFolders: async () => analyticsClient
    .get('/dashboard-folders')
    .then(unwrap),

  createDashboardFolder: async (name) => analyticsClient
    .post('/dashboard-folders', { name })
    .then(unwrap),

  renameDashboardFolder: async (oldName, newName) => analyticsClient
    .patch(`/dashboard-folders/${encodeURIComponent(oldName)}`, { newName })
    .then(unwrap),

  deleteDashboardFolder: async (name) => analyticsClient
    .delete(`/dashboard-folders/${encodeURIComponent(name)}`)
    .then(unwrap),
};

export { uid };
